/**
 * Gruppo Impresa Suite — configurazione centralizzata dei moduli.
 *
 * Fonte di verità unica per Launcher, "Altri Moduli", SuiteCard,
 * PWA manifest e comunicazioni. Ogni nuovo modulo si integra
 * modificando SOLO questo file.
 *
 * ARCHITETTURA (Fase 1 — attuale):
 *  - Un solo modulo è realmente sviluppato: Incassi & Pagamenti.
 *  - Studio e Presenze sono pianificati come moduli della Suite
 *    (non applicazioni indipendenti) e riutilizzeranno layout,
 *    autenticazione, componenti UI, notifiche di questo codebase.
 *  - CRM, Documentale, Paghe, AI Assistant, Portale Clienti sono
 *    nella roadmap commerciale ma non hanno una data prevista.
 *
 * DOPPIA MODALITÀ DI ACCESSO (Fase 1 — stub, infrastruttura Fase 2):
 *  - suite.gruppoimpresa.org → dashboard centrale con launcher
 *  - {modulo}.gruppoimpresa.org → ingresso diretto al modulo
 *  Entrambe le modalità dovranno convivere. Vedi `SUITE_BRAND.suiteUrl`
 *  e `module.url`; il routing effettivo su subdomini si abilita a
 *  livello di deploy (reverse proxy) senza modifiche al codebase.
 *
 * STATI POSSIBILI di un modulo:
 *  - "active"          → il modulo è pronto ed è quello in uso
 *  - "in-development"  → sviluppo attivo, non ancora rilasciato
 *  - "planned"         → in roadmap commerciale, senza data prevista
 */
import {
  Briefcase,
  Euro,
  CalendarDays,
  Users,
  FileText,
  Wallet,
  Sparkles,
  Globe2,
} from "lucide-react";

export const CURRENT_MODULE_KEY = "incassi";

export const SUITE_BRAND = {
  productName: "Gruppo Impresa Suite",
  license: "Professional",
  corporate: "Studio Bonaiuti & Gruppo Impresa STP a r.l.",
  suiteUrl: "https://suite.gruppoimpresa.org",
  // v1.60.6-final: rimosso il campo `version` (era "1.0", placeholder senza
  // reale versionamento della piattaforma). La versione mostrata nell'Hub
  // è quella del modulo corrente, letta da GET /api/version.
};

/** Etichette user-facing per gli stati dei moduli */
export const MODULE_STATUS_LABELS = {
  active: { label: "Attivo", tone: "positive" },
  "in-development": { label: "In sviluppo", tone: "info" },
  planned: { label: "Prossimamente", tone: "muted" },
};

export const SUITE_MODULES = [
  {
    key: "studio",
    name: "Gestionale di Studio",
    slug: "Studio",
    color: "#0B6F4F", // Verde Suite
    icon: Briefcase,
    url: "https://studio.gruppoimpresa.org",
    status: "active",
    tagline: "Amministrazione e gestione dello studio",
  },
  {
    key: "presenze",
    name: "Gestionale Presenze",
    slug: "Presenze",
    color: "#F97316", // Arancione
    icon: CalendarDays,
    url: "https://presenze.gruppoimpresa.org",
    status: "active",
    tagline: "Presenze, ore lavorate e assenze",
  },
  {
    key: "incassi",
    name: "Gestionale Incassi & Pagamenti",
    slug: "Incassi & Pagamenti",
    color: "#0D96BB", // Turchese Suite (modulo corrente)
    icon: Euro,
    url: "https://incassi.gruppoimpresa.org",
    status: "active",
    tagline: "Controllo economico, flussi e liquidità",
  },
  {
    key: "crm",
    name: "CRM",
    slug: "CRM",
    color: "#9333EA",
    icon: Users,
    url: "https://crm.gruppoimpresa.org",
    status: "planned",
    tagline: "Relazioni clienti, opportunità e pipeline",
  },
  {
    key: "documentale",
    name: "Documentale",
    slug: "Documentale",
    color: "#0EA5E9",
    icon: FileText,
    url: "https://docs.gruppoimpresa.org",
    status: "planned",
    tagline: "Archivio digitale e protocollo documenti",
  },
  {
    key: "paghe",
    name: "Paghe",
    slug: "Paghe",
    color: "#DC2626",
    icon: Wallet,
    url: "https://paghe.gruppoimpresa.org",
    status: "planned",
    tagline: "Buste paga, contributi e cedolini",
  },
  {
    key: "ai",
    name: "AI Assistant",
    slug: "AI Assistant",
    color: "#0F172A",
    icon: Sparkles,
    url: "https://ai.gruppoimpresa.org",
    status: "planned",
    tagline: "Assistente intelligente cross-modulo",
  },
  {
    key: "portale",
    name: "Portale Clienti",
    slug: "Portale Clienti",
    color: "#0891B2",
    icon: Globe2,
    url: "https://portale.gruppoimpresa.org",
    status: "planned",
    tagline: "Area riservata per i clienti dello studio",
  },
];

export const CURRENT_MODULE = SUITE_MODULES.find(
  (m) => m.key === CURRENT_MODULE_KEY,
);

export const ACTIVE_MODULES = SUITE_MODULES.filter((m) => m.status === "active");
export const IN_DEV_MODULES = SUITE_MODULES.filter(
  (m) => m.status === "in-development",
);
export const PLANNED_MODULES = SUITE_MODULES.filter(
  (m) => m.status === "planned",
);
